import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Wrench, ChefHat, Bath, PaintRoller, Layers, DoorOpen, Lightbulb, ArrowRight,
} from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import { CTASection } from "@/pages/Home";

const ICONS = [Wrench, ChefHat, Bath, PaintRoller, Layers, DoorOpen, Lightbulb];

export default function Services({ lang }) {
  const { t } = useTranslation();
  const prefix = lang === "es" ? "/es" : "";
  const items = t("servicesPage.items", { returnObjects: true });

  return (
    <>
      <section data-testid="services-hero" className="pt-40 pb-20 md:pt-52 md:pb-28 px-6 md:px-12 bg-navy text-offwhite">
        <div className="max-w-[1400px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <Kicker light>{t("servicesPage.kicker")}</Kicker>
            <h1 className="mt-6 font-heading font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
              {t("servicesPage.title")}
            </h1>
            <p className="mt-6 text-offwhite/60 max-w-2xl text-base md:text-lg leading-relaxed">{t("servicesPage.sub")}</p>
          </motion.div>
        </div>
      </section>

      <section data-testid="services-list" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-16">
          {items.map((item, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={i} delay={(i % 2) * 0.1}>
                <div className="group py-10 border-t border-sandline flex gap-6 md:gap-8">
                  <span className="font-heading font-black text-2xl text-sandline group-hover:text-orange transition-colors duration-300 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <div className="flex items-center gap-3">
                      <Icon size={22} strokeWidth={1.6} className="text-orange" />
                      <h2 className="font-heading font-bold text-2xl tracking-tight">{item.title}</h2>
                    </div>
                    <p className="mt-3 text-slateblue leading-relaxed">{item.desc}</p>
                    <Link
                      to={`${prefix}/estimate`}
                      data-testid={`service-estimate-link-${i}`}
                      className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-navy hover:text-orange transition-colors duration-200"
                    >
                      {t("servicesPage.cta")} <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <CTASection lang={lang} />
    </>
  );
}
